/**
 * Evidence Viewer Component
 *
 * Advanced image viewer with analysis tools:
 * - View image
 * - Download original
 * - Print
 * - Crop and analyze specific regions
 * - Zoom and pan
 */

import React, { useState, useRef, useEffect } from 'react';
import '../styles/EvidenceViewer.css';

function EvidenceViewer({ blobUrl, fileName, verificationCode }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [cropMode, setCropMode] = useState(false);
  const [cropStart, setCropStart] = useState(null);
  const [cropEnd, setCropEnd] = useState(null);
  const [showCropPreview, setShowCropPreview] = useState(false);

  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Reset states when URL changes
    setImageLoaded(false);
    setImageError(null);
    setZoom(1);
    setCropMode(false);
    setCropStart(null);
    setCropEnd(null);
  }, [blobUrl]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(null);
  };

  const handleImageError = (e) => {
    console.error('Image load error:', e);
    setImageError('Failed to load image');
    setImageLoaded(false);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || 'evidence-file.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Evidence: ${fileName}</title>
          <style>
            body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
            img { max-width: 100%; max-height: 100vh; }
            .header { text-align: center; padding: 20px; }
          </style>
        </head>
        <body>
          <div>
            <div class="header">
              <h2>${fileName}</h2>
              <p>Verification Code: ${verificationCode}</p>
              <p>Printed: ${new Date().toLocaleString()}</p>
            </div>
            <img src="${blobUrl}" onload="window.print(); window.close();" />
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoom(1);
  };

  const toggleCropMode = () => {
    if (cropMode) {
      // Exiting crop mode
      setCropStart(null);
      setCropEnd(null);
      setShowCropPreview(false);
    }
    setCropMode(!cropMode);
  };

  const handleMouseDown = (e) => {
    if (!cropMode || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCropStart({ x, y });
    setCropEnd({ x, y });
    setShowCropPreview(false);
  };

  const handleMouseMove = (e) => {
    if (!cropMode || !cropStart || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCropEnd({ x, y });
  };

  const handleMouseUp = () => {
    if (!cropMode || !cropStart || !cropEnd) return;
    // Crop selection complete
    setShowCropPreview(true);
  };

  const cancelCrop = () => {
    setCropStart(null);
    setCropEnd(null);
    setShowCropPreview(false);
  };

  const getCropStyles = () => {
    if (!cropStart || !cropEnd) return {};

    const left = Math.min(cropStart.x, cropEnd.x);
    const top = Math.min(cropStart.y, cropEnd.y);
    const width = Math.abs(cropEnd.x - cropStart.x);
    const height = Math.abs(cropEnd.y - cropStart.y);

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  };

  return (
    <div className="evidence-viewer-container">
      <div className="evidence-viewer-header">
        <h3>Evidence File Viewer</h3>
        <p className="evidence-viewer-filename">{fileName}</p>
        <p className="evidence-viewer-code">Verification Code: {verificationCode}</p>
      </div>

      <div className="evidence-viewer-toolbar">
        <button
          className="btn btn-icon"
          onClick={handleDownload}
          title="Download original file"
        >
          <span className="icon">📥</span> Download
        </button>

        <button
          className="btn btn-icon"
          onClick={handlePrint}
          title="Print evidence"
        >
          <span className="icon">🖨️</span> Print
        </button>

        <div className="toolbar-divider"></div>

        <button
          className="btn btn-icon"
          onClick={handleZoomOut}
          disabled={zoom <= 0.5}
          title="Zoom out"
        >
          <span className="icon">🔍−</span>
        </button>

        <span className="zoom-level">{Math.round(zoom * 100)}%</span>

        <button
          className="btn btn-icon"
          onClick={handleZoomIn}
          disabled={zoom >= 3}
          title="Zoom in"
        >
          <span className="icon">🔍+</span>
        </button>

        <button
          className="btn btn-icon"
          onClick={handleResetZoom}
          title="Reset zoom"
        >
          <span className="icon">⟲</span> Reset
        </button>

        <div className="toolbar-divider"></div>

        <button
          className={`btn btn-icon ${cropMode ? 'active' : ''}`}
          onClick={toggleCropMode}
          title="Crop and analyze region"
        >
          <span className="icon">✂️</span> {cropMode ? 'Exit Crop' : 'Crop'}
        </button>
      </div>

      {cropMode && (
        <div className="crop-instructions">
          <p>🔧 <strong>Crop Mode Active:</strong> Click and drag on the image to select a region for detailed analysis</p>
        </div>
      )}

      <div
        className="evidence-viewer-image-container"
        ref={containerRef}
        style={{
          cursor: cropMode ? 'crosshair' : 'default'
        }}
      >
        {!imageLoaded && !imageError && (
          <div className="evidence-viewer-loading">
            <div className="spinner"></div>
            <p>Loading evidence file...</p>
          </div>
        )}

        {imageError && (
          <div className="evidence-viewer-error">
            <p>❌ {imageError}</p>
            <button className="btn btn-primary" onClick={() => setImageError(null)}>
              Retry
            </button>
          </div>
        )}

        <div
          className="image-wrapper"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          style={{
            position: 'relative',
            display: imageLoaded ? 'inline-block' : 'none'
          }}
        >
          <img
            ref={imageRef}
            src={blobUrl}
            alt={fileName}
            className="evidence-image"
            onLoad={handleImageLoad}
            onError={handleImageError}
            crossOrigin="anonymous"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'center center',
              transition: 'transform 0.2s ease',
              maxWidth: '100%',
              height: 'auto',
              userSelect: 'none'
            }}
            draggable={false}
          />

          {cropMode && cropStart && cropEnd && (
            <div
              className="crop-selection"
              style={getCropStyles()}
            />
          )}
        </div>
      </div>

      {showCropPreview && cropStart && cropEnd && imageRef.current && (
        <div className="crop-preview-modal" onClick={cancelCrop}>
          <div className="crop-preview-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
            <h4>Cropped Region Preview</h4>
            <p style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
              📸 Use your device's screenshot tool to capture this cropped region
            </p>

            <div style={{
              position: 'relative',
              overflow: 'hidden',
              maxWidth: '100%',
              maxHeight: '60vh',
              border: '2px solid #3b82f6',
              borderRadius: '8px',
              background: '#f9fafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={blobUrl}
                alt="Cropped region"
                style={{
                  display: 'block',
                  position: 'relative',
                  marginLeft: `-${Math.min(cropStart.x, cropEnd.x)}px`,
                  marginTop: `-${Math.min(cropStart.y, cropEnd.y)}px`,
                  width: `${imageRef.current.width}px`,
                  height: `${imageRef.current.height}px`,
                  maxWidth: 'none'
                }}
              />
            </div>

            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              fontSize: '0.85rem',
              color: '#1e40af'
            }}>
              <strong>💡 How to save:</strong>
              <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: 0 }}>
                <li><strong>Windows:</strong> Press Win + Shift + S, then select the image area</li>
                <li><strong>Mac:</strong> Press Cmd + Shift + 4, then select the image area</li>
                <li><strong>Mobile:</strong> Use your device's screenshot button combination</li>
              </ul>
            </div>

            <div className="crop-preview-actions" style={{ marginTop: '1.5rem' }}>
              <button className="btn btn-secondary" onClick={cancelCrop}>
                ✓ Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EvidenceViewer;
